import React, { useMemo, useState } from "react";
import CountUp from "react-countup";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

/**
 * Per-institution-category statistics.
 *
 * A category is an institutional block (e.g. "Entreprise étatique") and its
 * institutions are the detail (e.g. "Ministère de la Justice"). Institutions
 * are collapsed by default to keep the view readable when a category holds
 * many of them.
 */
function CategoryCard({ category }) {
  const [expanded, setExpanded] = useState(false);

  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories
    : [];
  const filledSubcategories = subcategories.filter((sub) => sub.nb_users > 0);

  return (
    <Col xl={4} md={6}>
      <Card className="card-animate h-100">
        <CardBody>
          <div className="d-flex align-items-start justify-content-between">
            <div className="flex-grow-1 overflow-hidden">
              <h6 className="fs-15 fw-semibold mb-1 text-truncate">
                {category.category_name}
              </h6>
              {category.user_type ? (
                <span className="badge bg-primary-subtle text-primary">
                  {category.user_type}
                </span>
              ) : null}
            </div>
            <div className="flex-shrink-0 ms-2">
              <div className="avatar-sm rounded-circle bg-info-subtle d-flex align-items-center justify-content-center">
                <i className="ri-building-line fs-4 text-info" />
              </div>
            </div>
          </div>

          <Row className="mt-4 g-0 text-center">
            <Col xs={6} className="border-end">
              <p className="text-muted text-uppercase fw-medium fs-11 mb-1">
                Utilisateurs
              </p>
              <h4 className="mb-0 ff-secondary fw-semibold">
                <CountUp
                  start={0}
                  end={category.nb_users}
                  duration={1}
                  separator=" "
                />
              </h4>
            </Col>
            <Col xs={6}>
              <p className="text-muted text-uppercase fw-medium fs-11 mb-1">
                Certifiés
              </p>
              <h4 className="mb-0 ff-secondary fw-semibold text-success">
                <CountUp
                  start={0}
                  end={category.nb_certified}
                  duration={1}
                  separator=" "
                />
              </h4>
            </Col>
          </Row>

          <div className="mt-3 pt-3 border-top">
            {subcategories.length === 0 ? (
              <p className="text-muted fs-12 mb-0 fst-italic">
                Aucune institution rattachée
              </p>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => setExpanded((value) => !value)}
                >
                  <i
                    className={`ri-arrow-${
                      expanded ? "down" : "right"
                    }-s-line align-bottom me-1`}
                  />
                  {subcategories.length} institution
                  {subcategories.length > 1 ? "s" : ""}
                  {filledSubcategories.length > 0
                    ? ` (${filledSubcategories.length} avec utilisateurs)`
                    : ""}
                </button>

                {expanded ? (
                  <ul className="list-unstyled mt-2 mb-0 vstack gap-1">
                    {subcategories.map((sub) => (
                      <li
                        key={sub.subcategory_id}
                        className="d-flex justify-content-between align-items-center fs-12"
                      >
                        <span className="text-truncate me-2">
                          {sub.subcategory_name}
                        </span>
                        <span className="badge bg-light text-body flex-shrink-0">
                          {sub.nb_users}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
}

function CategoriesStats({ dashboard }) {
  const categories = useMemo(
    () =>
      Array.isArray(dashboard?.categories_stats)
        ? dashboard.categories_stats
        : [],
    [dashboard?.categories_stats]
  );

  if (categories.length === 0) return null;

  return (
    <Col xs={12}>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            Catégories d&apos;institutions
          </h4>
          <span className="text-muted fs-12">
            {categories.length} catégorie{categories.length > 1 ? "s" : ""}
          </span>
        </CardHeader>
        <CardBody className="pt-0">
          <p className="text-muted fs-12 mb-3">
            Répartition des utilisateurs par bloc institutionnel. Chaque
            catégorie regroupe les institutions qui lui sont rattachées.
          </p>
          <Row>
            {categories.map((category) => (
              <CategoryCard key={category.category_id} category={category} />
            ))}
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
}

export default CategoriesStats;
